import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { objectStorageApi } from '../../../api/objectStorage';
import footerLight from '../../../assets/postpop-footer-light.png';
import footerDark from '../../../assets/postpop-footer-dark.png';

const endpoint = '/admin/ads/splash';
const empty = {
  title: '',
  displayTitle: '',
  displayDescription: '',
  mode: 'FULL_SCREEN',
  imageUrl: '',
  destinationUrl: '',
  enabled: false,
  startsAt: '',
  endsAt: '',
};
const localDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
export default function SplashAdManagement() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const draft = useAdminDraftGuard({ form, file }, { enabled: form !== null, busy });
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getAuth(endpoint);
      setSettings(data.data);
    } catch {
      setError('스플래시 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const edit = () => {
    const value = settings || empty;
    setForm({
      ...empty,
      ...value,
      startsAt: localDate(value.startsAt),
      endsAt: localDate(value.endsAt),
    });
    setFile(null);
    setError('');
    setSaved(false);
  };
  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (busy || !form) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        displayTitle: form.displayTitle.trim(),
        displayDescription: form.displayDescription.trim(),
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      };
      if (!payload.title || payload.title.length > 160)
        throw new Error('관리용 설명은 1~160자입니다.');
      if (payload.displayTitle.length > 80 || payload.displayDescription.length > 160)
        throw new Error('표시 제목은 80자, 설명은 160자 이하여야 합니다.');
      if (payload.startsAt && payload.endsAt && payload.startsAt >= payload.endsAt)
        throw new Error('종료 시각은 시작 시각보다 늦어야 합니다.');
      if (file) {
        const body = new FormData();
        body.append('image', file);
        const uploaded = await objectStorageApi.uploadImage(body);
        if (!uploaded.data?.url) throw new Error('이미지 업로드 결과가 없습니다.');
        payload.imageUrl = uploaded.data.url;
        setForm((current) => ({ ...current, imageUrl: payload.imageUrl }));
        setFile(null);
      }
      if (!payload.imageUrl) throw new Error('스플래시 이미지를 선택해주세요.');
      const { data } = await api.putAuth(endpoint, payload);
      setSettings(data.data);
      setForm(null);
      setSaved(true);
    } catch (failure) {
      setError(failure.response?.data?.msg || failure.message || '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };
  const bottom = form?.mode === 'BOTTOM_HALF';
  return (
    <Stack spacing={2}>
      <Typography variant="h6">스플래시 광고</Typography>
      <Alert severity="info">
        앱 시작 시 공개 여부와 이미지 준비 상태를 확인합니다. 새 이미지가 아직 준비되지 않았다면
        이번 실행은 생략하고, 이미지가 준비된 다음 완전 실행부터 표시할 수 있습니다.
      </Alert>
      <Typography>
        완전 실행당 1회 · 전면형 약 2.3초(0.4초 페이드 포함), 하단형 약 2.6초 후 자동 종료 ·
        건너뛰기 및 링크 이동 없음 · 표시한 실행의 첫 접속 팝업 생략. 준비되지 않은 광고는 홈 진입을
        지연시키지 않습니다.
      </Typography>
      {error && (
        <Alert severity="error" action={!form ? <Button onClick={load}>재시도</Button> : null}>
          {error}
        </Alert>
      )}
      {saved && <Alert severity="success">저장했습니다.</Alert>}
      {!form ? (
        <>
          {loading ? (
            <Typography>설정을 불러오는 중…</Typography>
          ) : (
            settings && (
              <Typography>
                {settings.title || '등록된 스플래시 광고 없음'} ·{' '}
                {settings.enabled ? '공개 설정' : '비공개'}
              </Typography>
            )
          )}
          <Button disabled={loading || busy || !settings} onClick={edit}>
            스플래시 설정
          </Button>
        </>
      ) : (
        <Box component="form" aria-label="스플래시 광고 편집" onSubmit={save}>
          <Stack
            spacing={2}
            component="fieldset"
            disabled={busy}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <TextField
              required
              label="관리·화면 읽기용 설명"
              value={form.title}
              inputProps={{ maxLength: 160 }}
              onChange={(e) => change('title', e.target.value)}
            />
            <TextField
              select
              label="표시 형식"
              value={form.mode}
              onChange={(e) => change('mode', e.target.value)}
            >
              <MenuItem value="FULL_SCREEN">기존 스플래시 이후 전면 광고</MenuItem>
              <MenuItem value="BOTTOM_HALF">기존 스플래시 하단 결합 (최대 50%)</MenuItem>
            </TextField>
            <Typography variant="caption">
              {bottom
                ? '기존 하단 postpop 로고를 상단에 크게 고정합니다. 광고 이미지 4:3, 권장 1200×900px. 이미지 위 제목과 아래 설명은 선택 입력입니다.'
                : '전체 화면에 중앙 잘림으로 표시합니다. 9:19.5 화면 기준 권장 1080×2340px.'}
              기기 비율에 따라 잘릴 수 있으므로 중요한 문구는 가장자리를 피해주세요.
            </Typography>
            {bottom && (
              <>
                <TextField
                  label="이미지 위 제목 (선택)"
                  multiline
                  minRows={2}
                  value={form.displayTitle}
                  inputProps={{ maxLength: 80 }}
                  helperText="최대 80자. 앱에서는 최대 2줄이며 넘치는 문구는 말줄임 처리합니다. 비우면 표시하지 않습니다."
                  onChange={(e) => change('displayTitle', e.target.value)}
                />
                <TextField
                  label="이미지 아래 설명 (선택)"
                  multiline
                  minRows={2}
                  value={form.displayDescription}
                  inputProps={{ maxLength: 160 }}
                  helperText="최대 160자. 앱에서는 최대 2줄이며 넘치는 문구는 말줄임 처리합니다. 전면형에서는 제목·설명 모두 표시하지 않습니다."
                  onChange={(e) => change('displayDescription', e.target.value)}
                />
              </>
            )}
            <Button component="label" variant="outlined">
              이미지 선택
              <input
                hidden
                aria-label="스플래시 이미지 파일"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const selected = e.target.files?.[0];
                  e.target.value = '';
                  if (!selected) return;
                  if (
                    !['image/png', 'image/jpeg', 'image/webp'].includes(selected.type) ||
                    selected.size > 5 * 1024 * 1024
                  ) {
                    setError('PNG/JPG/WebP 5MB 이하 이미지를 선택해주세요.');
                    return;
                  }
                  setError('');
                  setFile(selected);
                }}
              />
            </Button>
            <TextField
              label="공개 시작 (선택, 관리자 기기 시간)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.startsAt}
              onChange={(e) => change('startsAt', e.target.value)}
            />
            <TextField
              label="공개 종료 (선택, 관리자 기기 시간)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.endsAt}
              onChange={(e) => change('endsAt', e.target.value)}
            />
            {(preview || form.imageUrl) && (
              <Box
                aria-label="스플래시 배치 미리보기"
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 270,
                  aspectRatio: '9 / 19.5',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                  containerType: 'inline-size',
                }}
              >
                {bottom && (
                  <Box
                    aria-label="기존 하단 postpop 로고 (고정)"
                    sx={{
                      position: 'absolute',
                      top: 'calc(31% - 7.589cqw)',
                      left: '21.1%',
                      width: '57.8%',
                      height: '15.177cqw',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      alt="postpop"
                      src={footerLight}
                      sx={{
                        display: (theme) => (theme.palette.mode === 'dark' ? 'none' : 'block'),
                        position: 'absolute',
                        width: '118.162%',
                        height: '450%',
                        left: '-9.081%',
                        top: '-187.917%',
                      }}
                    />
                    <Box
                      component="img"
                      alt=""
                      src={footerDark}
                      sx={{
                        display: (theme) => (theme.palette.mode === 'dark' ? 'block' : 'none'),
                        position: 'absolute',
                        width: '118.162%',
                        height: '450%',
                        left: '-9.081%',
                        top: '-187.917%',
                      }}
                    />
                  </Box>
                )}
                {bottom && form.displayTitle.trim() && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '10%',
                      width: '80%',
                      bottom: 'calc(17.4% + 51cqw + 3.054cqw)',
                      height: '14.25cqw',
                      display: 'flex',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Typography
                      data-testid="splash-preview-title"
                      sx={{
                        width: '100%',
                        fontSize: '5.089cqw',
                        lineHeight: '7.125cqw',
                        fontWeight: 600,
                        color: 'text.primary',
                        textAlign: 'center',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {form.displayTitle.trim()}
                    </Typography>
                  </Box>
                )}
                <Box
                  component="img"
                  alt="스플래시 광고 미리보기"
                  src={preview || form.imageUrl}
                  sx={{
                    position: 'absolute',
                    left: bottom ? '16%' : 0,
                    bottom: bottom ? '17.4%' : 0,
                    width: bottom ? '68%' : '100%',
                    height: bottom ? 'auto' : '100%',
                    aspectRatio: bottom ? '4 / 3' : undefined,
                    borderRadius: bottom ? '3.054cqw' : 0,
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
                {bottom && form.displayDescription.trim() && (
                  <Typography
                    data-testid="splash-preview-description"
                    sx={{
                      position: 'absolute',
                      left: '10%',
                      width: '80%',
                      top: 'calc(82.6% + 3.562cqw)',
                      fontSize: '3.562cqw',
                      lineHeight: '5.089cqw',
                      color: 'text.secondary',
                      textAlign: 'center',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {form.displayDescription.trim()}
                  </Typography>
                )}
                <Box
                  data-testid="splash-preview-ad"
                  sx={{
                    position: 'absolute',
                    right: bottom ? 'calc(16% + 1.527cqw)' : 12,
                    bottom: bottom ? 'calc(17.4% + 1.527cqw)' : 12,
                    bgcolor: 'rgba(0,0,0,.25)',
                    color: '#fff',
                    px: '0.763cqw',
                    py: '0.254cqw',
                    fontSize: '2.036cqw',
                    lineHeight: '2.545cqw',
                    borderRadius: '0.763cqw',
                  }}
                >
                  AD
                </Box>
              </Box>
            )}
            <FormControlLabel
              label="스플래시 공개"
              control={
                <Checkbox
                  checked={form.enabled}
                  onChange={(e) => change('enabled', e.target.checked)}
                />
              }
            />
            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => {
                  if (!draft.confirmDiscard()) return;
                  setForm(null);
                  setFile(null);
                  setError('');
                }}
              >
                취소
              </Button>
              <Button type="submit" variant="contained">
                {busy ? '저장 중…' : '저장'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
